import { useEffect, useState } from "react";
import InputErrorMsg from "../../form-builder-helpers/InputErrorMsg";
import SvgPencil from "./SvgPencil";
import SvgVerified from "./SvgVerified";
import RequiredStar from "@/app/components/lib/RequiredStar";
import { useLang } from "@/providers/langProvider";
import useIsRtl from "@/hooks/useIsRtl";
import { Controller } from "react-hook-form";

interface Props {
  label: string;
  name: string;
  control: any; // React Hook Form control
  type?: string;
  dir?: "ltr" | "rtl" | "auto";
  errorMsg?: string;
  viewPencil?: boolean;
  verifiedPosition?: number | null;
  required?: boolean;
  placeholder?: string;
  hideLabel?: boolean;
  hint?: string;
  multiLang?: boolean;
  rules?: any; // Validation rules for Controller
}

const HookInputMultiLang: React.FC<Props> = ({
  label,
  name,
  control,
  type = "text",
  dir = "auto",
  errorMsg,
  viewPencil = false,
  verifiedPosition = null,
  required = false,
  placeholder,
  hideLabel,
  hint,
  multiLang = false,
  rules,
}) => {
  const locale = useLang();
  const isRtl = useIsRtl();
  const [focused, setFocused] = useState(false);
  const [selectedLang, setSelectedLang] = useState<"en" | "ar">(
    locale as "en" | "ar",
  );

  // Set initial selected language based on locale
  useEffect(() => {
    if (multiLang) {
      setSelectedLang(locale as "en" | "ar");
    }
  }, [locale, multiLang]);

  const parseMultiLangValue = (value: any) => {
    if (!value) return { en: "", ar: "" };
    try {
      return typeof value === "string" ? JSON.parse(value) : value;
    } catch (e) {
      const defaultVal = { en: "", ar: "" };
      if (typeof value === "string") {
        try {
          const trimmed = value.trim();
          if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
            return defaultVal;
          }
        } catch (_) {}
      }
      return defaultVal;
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const multiLangValue = multiLang
          ? parseMultiLangValue(field.value)
          : null;

        const handleMultiLangChange = (
          e: React.ChangeEvent<HTMLInputElement>,
        ) => {
          const newValue = e.target.value;
          const updatedValue = {
            ...multiLangValue,
            [selectedLang]: newValue,
          };
          field.onChange(JSON.stringify(updatedValue));
        };

        const getCurrentValue = () => {
          if (multiLang && multiLangValue) {
            return multiLangValue[selectedLang] || "";
          }
          return field.value || "";
        };

        const inputProps = multiLang
          ? {
              ...field,
              value: getCurrentValue(),
              onChange: handleMultiLangChange,
            }
          : field;

        return (
          <div className="form-field">
            {multiLang && (
              <div className="lang-toggle" dir={isRtl ? "rtl" : "ltr"}>
                <button
                  type="button"
                  className={`lang-btn ${
                    selectedLang === "en" ? "active" : ""
                  }`}
                  onClick={() => setSelectedLang("en")}
                >
                  EN
                </button>
                <button
                  type="button"
                  className={`lang-btn ${
                    selectedLang === "ar" ? "active" : ""
                  }`}
                  onClick={() => setSelectedLang("ar")}
                >
                  AR
                </button>
              </div>
            )}

            <div className="form-floating" dir={isRtl ? "rtl" : "ltr"}>
              <input
                className="form-control"
                id={`input-${name.replaceAll("(", "_").replaceAll(")", "_")}`}
                {...inputProps}
                type={type}
                placeholder={!hideLabel && !focused ? "" : placeholder ?? label}
                dir={multiLang && selectedLang === "ar" ? "rtl" : dir}
                autoComplete="new-password"
                onFocus={() => setFocused(true)}
                onBlur={(_e) => {
                  setFocused(false);
                  field.onBlur();
                }}
              />
              <label
                htmlFor={`input-${name.replaceAll("(", "_").replaceAll(")", "_")}`}
              >
                {!hideLabel && (
                  <span>
                    <RequiredStar title={label} isRequired={required} />
                  </span>
                )}
                {viewPencil && !focused && locale !== "ar" && (
                  <div className="pencil">
                    <SvgPencil />
                  </div>
                )}
                {verifiedPosition && (
                  <div
                    className="verified"
                    style={{
                      marginInlineStart:
                        label && locale === "ar"
                          ? `calc(100% - ${verifiedPosition + 24}px)`
                          : verifiedPosition + 4,
                    }}
                  >
                    <SvgVerified />
                  </div>
                )}
              </label>
              {hint && <div className="field-hint">{hint}</div>}
              {errorMsg && <InputErrorMsg errorMsg={errorMsg} />}
            </div>
          </div>
        );
      }}
    />
  );
};
export default HookInputMultiLang;
